import cn from '@sindresorhus/class-names';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import {useCallback, useRef} from 'react';

import ArrowBackIcon from '@web-landing/assets/images/search/arrow-back.svg';
import SearchImage from '@web-landing/assets/images/search/inputSearch.svg';

import {searchInputTestIds} from '@web-landing/constants/test-ids/mainLandingTestIds';

import {SearchBlockType} from '@strapi/types/MainLanding';
import {SearchBarLocation} from '@web-landing/analytics/types/analytics';

import {useSearchAnalytics} from '@web-landing/analytics/hooks/useSearchAnalytics';
import {useSearch} from '@web-landing/hooks/useSearch';

import styles from './SearchInput.module.scss';

const AppsList = dynamic(
  async () =>
    import('@web-landing/components/SearchPicks/SearchBlock/AppsList/AppsList'),
);

export const SearchInput = ({
  requestUrl,
  isMobile,
  handleHideSearch,
  targetLocation,
  autoFocus,
  searchBlock,
  withoutLayout,
  isMobileMenu,
}: {
  requestUrl: string | undefined;
  isMobile?: boolean;
  handleHideSearch?: () => void;
  targetLocation: SearchBarLocation;
  autoFocus?: boolean;
  withoutLayout?: boolean;
  searchBlock: SearchBlockType;
  isMobileMenu?: boolean;
}) => {
  const {
    placeholder = 'Поиск приложений и игр',
    searchButtonText = 'Найти',
    clearHistoryButton = 'Очистить историю',
  } = searchBlock;
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    inputText,
    historyList,
    isInputActive,
    suggestionsList,
    setHistory,
    deleteHistoryItem,
    setClearHistory,
    setShowAppsList,
    handleInputChange,
    handleInputEnterClick,
    handleSearchButtonClick,
    searchBarHistoryShowAnalytics,
    setHideAppsList,
  } = useSearch({requestUrl, targetLocation, autoFocus});

  const {searchBarSuggestionsClickAnalytics, handleInputClickAnalytics} =
    useSearchAnalytics(targetLocation);

  const setClearHistoryWrapped = useCallback(() => {
    setClearHistory();
    inputRef.current?.focus();
  }, [setClearHistory]);

  return (
    <div
      className={cn(styles.search, {[styles.mobileMenu]: isMobileMenu})}
      onClick={setShowAppsList}
      data-testid={searchInputTestIds.searchInput}
    >
      <div className={styles.inputWrapper}>
        {isMobile && (
          <Image
            src={ArrowBackIcon}
            alt="back"
            className={styles.image}
            onClick={handleHideSearch}
            data-testid={searchInputTestIds.searchInputBackButton}
          />
        )}

        {!isMobile && (
          <Image src={SearchImage} alt="search" className={styles.image} />
        )}

        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          name="rearch"
          placeholder={placeholder}
          autoComplete="off"
          onChange={handleInputChange}
          onClick={handleInputClickAnalytics}
          onKeyDown={handleInputEnterClick}
          data-testid={searchInputTestIds.searchInput}
          autoFocus={autoFocus}
        />
      </div>
      <a
        href={`https://www.rustore.ru/catalog/search?query=${inputText}`}
        onClick={handleSearchButtonClick}
        className={cn(styles.button, {
          [styles.buttonDisabled]: !inputText.trim().length,
        })}
        data-testid={searchInputTestIds.searchButton}
      >
        {searchButtonText}
      </a>
      {(isInputActive || isMobile) && (
        <>
          {!withoutLayout && (
            <div
              className={styles.root}
              onClick={setHideAppsList}
              data-testid={searchInputTestIds.searchHideAppsList}
            />
          )}
          <AppsList
            suggestionsList={suggestionsList}
            inputText={inputText}
            setHistoryList={setHistory}
            historyList={historyList}
            setClearHistory={setClearHistoryWrapped}
            deleteHistoryItem={deleteHistoryItem}
            isMobile={isMobile}
            searchBarHistoryShowAnalytics={searchBarHistoryShowAnalytics}
            searchBarSuggestionsClickAnalytics={
              searchBarSuggestionsClickAnalytics
            }
            clearHistoryButton={clearHistoryButton}
          />
        </>
      )}
    </div>
  );
};
